import { isValidCPF, isValidCNPJ, validateCpfCnpj } from './cpfCnpjValidator';

/**
 * Aplica máscara de CPF: 000.000.000-00
 */
export const applyCpfMask = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 3) return cleanValue;
  if (cleanValue.length <= 6) return `${cleanValue.slice(0, 3)}.${cleanValue.slice(3)}`;
  if (cleanValue.length <= 9) return `${cleanValue.slice(0, 3)}.${cleanValue.slice(3, 6)}.${cleanValue.slice(6)}`;
  return `${cleanValue.slice(0, 3)}.${cleanValue.slice(3, 6)}.${cleanValue.slice(6, 9)}-${cleanValue.slice(9, 11)}`;
};

/**
 * Aplica máscara de CNPJ: 00.000.000/0000-00
 */
export const applyCnpjMask = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 2) return cleanValue;
  if (cleanValue.length <= 5) return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2)}`;
  if (cleanValue.length <= 8) return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2, 5)}.${cleanValue.slice(5)}`;
  if (cleanValue.length <= 12) return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2, 5)}.${cleanValue.slice(5, 8)}/${cleanValue.slice(8)}`;
  return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2, 5)}.${cleanValue.slice(5, 8)}/${cleanValue.slice(8, 12)}-${cleanValue.slice(12, 14)}`;
};

/**
 * Aplica máscara de CEP: 00000-000
 */
export const applyCepMask = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 5) return cleanValue;
  return `${cleanValue.slice(0, 5)}-${cleanValue.slice(5, 8)}`;
};

/**
 * Aplica máscara de Idade (apenas números, máximo 3 dígitos)
 */
export const applyIdadeMask = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  return cleanValue.slice(0, 3);
};

/**
 * Aplica máscara de Data de Nascimento: DD/MM/AAAA
 */
export const applyDataNascimentoMask = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  if (cleanValue.length <= 2) return cleanValue;
  if (cleanValue.length <= 4) return `${cleanValue.slice(0, 2)}/${cleanValue.slice(2)}`;
  return `${cleanValue.slice(0, 2)}/${cleanValue.slice(2, 4)}/${cleanValue.slice(4, 8)}`;
};

/**
 * Valida CPF
 */
export const validateCPF = (cpf: string): boolean => {
  if (!cpf) return false;
  return isValidCPF(cpf);
};

/**
 * Valida CNPJ
 */
export const validateCNPJ = (cnpj: string): boolean => {
  if (!cnpj) return false;
  return isValidCNPJ(cnpj);
};

/**
 * Valida CEP (8 dígitos)
 */
export const validateCEP = (cep: string): boolean => {
  if (!cep) return false;
  const cleanCep = cep.replace(/\D/g, '');
  return cleanCep.length === 8;
};

/**
 * Valida Idade (entre 0 e 150)
 */
export const validateIdade = (idade: string): boolean => {
  if (!idade) return false;
  const numIdade = parseInt(idade, 10);
  return !isNaN(numIdade) && numIdade >= 0 && numIdade <= 150;
};

/**
 * Valida Data de Nascimento
 */
export const validateDataNascimento = (data: string): boolean => {
  if (!data) return false;
  const cleanData = data.replace(/\D/g, '');
  if (cleanData.length !== 8) return false;
  
  const dia = parseInt(cleanData.slice(0, 2), 10);
  const mes = parseInt(cleanData.slice(2, 4), 10);
  const ano = parseInt(cleanData.slice(4, 8), 10);
  
  if (dia < 1 || dia > 31) return false;
  if (mes < 1 || mes > 12) return false;
  if (ano < 1900 || ano > new Date().getFullYear()) return false;
  
  const dataObj = new Date(ano, mes - 1, dia);
  return dataObj.getDate() === dia && 
         dataObj.getMonth() === mes - 1 && 
         dataObj.getFullYear() === ano;
};

/**
 * Aplica máscara de CPF/CNPJ (detecta automaticamente durante a digitação)
 */
export const applyCpfCnpjMaskAuto = (value: string): string => {
  const cleanValue = value.replace(/\D/g, '');
  
  // Se tem até 11 dígitos, formata como CPF
  if (cleanValue.length <= 11) {
    if (cleanValue.length <= 3) return cleanValue;
    if (cleanValue.length <= 6) return `${cleanValue.slice(0, 3)}.${cleanValue.slice(3)}`;
    if (cleanValue.length <= 9) return `${cleanValue.slice(0, 3)}.${cleanValue.slice(3, 6)}.${cleanValue.slice(6)}`;
    return `${cleanValue.slice(0, 3)}.${cleanValue.slice(3, 6)}.${cleanValue.slice(6, 9)}-${cleanValue.slice(9, 11)}`;
  }
  
  // Se tem mais de 11 dígitos, formata como CNPJ
  if (cleanValue.length <= 14) {
    if (cleanValue.length <= 2) return cleanValue;
    if (cleanValue.length <= 5) return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2)}`;
    if (cleanValue.length <= 8) return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2, 5)}.${cleanValue.slice(5)}`;
    if (cleanValue.length <= 12) return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2, 5)}.${cleanValue.slice(5, 8)}/${cleanValue.slice(8)}`;
    return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2, 5)}.${cleanValue.slice(5, 8)}/${cleanValue.slice(8, 12)}-${cleanValue.slice(12, 14)}`;
  }
  
  // Limita a 14 dígitos
  return `${cleanValue.slice(0, 2)}.${cleanValue.slice(2, 5)}.${cleanValue.slice(5, 8)}/${cleanValue.slice(8, 12)}-${cleanValue.slice(12, 14)}`;
};

/**
 * Valida CPF ou CNPJ automaticamente
 */
export const validateCpfCnpjAuto = (document: string): boolean => {
  if (!document) return false;
  const result = validateCpfCnpj(document);
  return result.isValid;
};

